import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import React from "react";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    address: string;
    extendedStyles?: PaymentProfileBillingAddressStyles;
    cardNumber?:string;
    nameOnCard?:string;
    expiryDate?:string;
}

export interface PaymentProfileBillingAddressStyles {
    wrapper?: InjectableCss;
    label?: TypographyPresentationProps;
    text?: TypographyPresentationProps;
    nameOnCardLabelText?: TypographyPresentationProps;
    descriptionText?: TypographyPresentationProps;
    nameOnCardText?: TypographyPresentationProps;
    expirationContainer?: InjectableCss;
    expirationText?: TypographyPresentationProps;
    cardAddress?: InjectableCss;
    billingAddressLabelText?: TypographyPresentationProps;
    billingAddressText?: TypographyPresentationProps;
    expirationLabelText?: TypographyPresentationProps;
}

export const paymentProfileBillingAddressStyles: PaymentProfileBillingAddressStyles = {
    label: { weight: 600 },
    wrapper: {
        css: css`
            .card-heading {
                color: ${themeColors.colorHeadText};
                font-family: 'Roboto', sans-serif;
                font-size: ${themeFontSizes.size18};
                font-weight: ${themeFontWeights.fontWeightMedium};
                letter-spacing: 0;
                line-height: 1.4375rem;
                margin-bottom: .9375rem;
                margin-top: 0.3125rem;
            }

            .border-seperator {
                border-bottom: 0.0625rem solid ${themeColors.colorBorderBrands};
                padding-top: 0;
                margin-top: 1.25rem;
                margin-bottom: 1.25rem;
                width: 100%;
                display: block;
            }
        `,
    },
    nameOnCardLabelText: {
        weight: "bold",
        css: css`
            margin-bottom: .250625rem;
            span:first-child {
                min-width: 6.875rem;
                display: inline-block;
            }
        `,
    },
    nameOnCardText: {
        css: css`
            margin: 0 0.625rem;
            color: ${themeColors.colorAddress};
            font-size: ${themeFontSizes.size16};
            font-family: 'Lato',sans-serif;
            letter-spacing: 0;
            line-height: 1.5rem;
            font-weight: ${themeFontWeights.fontWeightNormal};
            display: inline-block;
        `,
    },
    expirationLabelText: {
        weight: "bold",
        css: css`
            margin-bottom: .250625rem;
            margin-top: .250625rem;
            min-width: 6.875rem;
            display: inline-block;
            span {
                width: 100%;
            }
        `,
    },
    expirationText: {
        css: css`
            color: ${themeColors.colorAddress};
            font-size: ${themeFontSizes.size16};
            font-family: 'Lato',sans-serif;
            letter-spacing: 0;
            line-height: 1.5rem;
            font-weight: ${themeFontWeights.fontWeightNormal};
            display: inline-block;
            width: 100%;
        `,
    },
    descriptionText: {
        css: css`
            margin: 0 0.625rem;
            color: ${themeColors.colorAddress};
            font-size: ${themeFontSizes.size16};
            font-family: 'Lato',sans-serif;
            letter-spacing: 0;
            line-height: 1.5rem;
            font-weight: ${themeFontWeights.fontWeightNormal};
            display: inline-block;
        `,
    },
    billingAddressLabelText: {
        weight: "bold",
        css: css`
            margin-bottom: .250625rem;
            color: ${themeColors.colorHeadText};
            font-family: 'Roboto', sans-serif;
            font-size: ${themeFontSizes.size16};
            font-weight: ${themeFontWeights.fontWeightMedium};
            letter-spacing: 0;
            line-height: 1.4375rem;
        `,
    },
    billingAddressText: {
        css: css`
            width: 100%;
            margin-left: 0;
            color: ${themeColors.colorAddress};
            font-family: 'Lato',sans-serif;
            font-size: ${themeFontSizes.size16};
            letter-spacing: 0;
            line-height: 1.5rem;
        `,
    },
    expirationContainer: {
        css: css`
            width: 100%;
        `,
    },
};

const PaymentProfileBillingAddress = (props: OwnProps) => {
    const [styles] = React.useState(() => mergeToNew(paymentProfileBillingAddressStyles, props.extendedStyles));

    return (
        <StyledWrapper {...styles.wrapper} className="select-card-wrapper" as="div">
            <Typography as="div" className="card-number" {...styles.nameOnCardLabelText} >
                <Typography {...styles.label} id="paymentProfileCardNumber">
                    {translate("Card Number")}:
                </Typography>
                <Typography {...styles.descriptionText} aria-labelledby="paymentProfileCardNumber">
                    {props.cardNumber && (`${translate("************")}${props.cardNumber.substring(props.cardNumber.length - 4)}`)} 
                </Typography>
            </Typography>
            <Typography as="div" className="card-name" {...styles.nameOnCardLabelText}>
                <Typography {...styles.label} id="paymentProfileNameOnCard">
                    {translate("Name on Card")}:
                </Typography>
                <Typography {...styles.nameOnCardText} aria-labelledby="paymentProfileNameOnCard" {...styles.nameOnCardText}>
                    {props.nameOnCard}
                </Typography>
            </Typography>
            <Typography as="div" className="card-date" {...styles.expirationContainer}>
                <Typography className="card-month" {...styles.expirationLabelText}>
                    <Typography {...styles.label}>
                        {translate("Exp. Month")}:
                    </Typography>
                    <Typography {...styles.expirationText} aria-labelledby="paymentProfileExpiryDate">
                        {props.expiryDate &&
                            props.expiryDate.substring(0, props.expiryDate.lastIndexOf("/"))
                        }

                    </Typography>
                </Typography>

                <Typography className="card-year" {...styles.expirationLabelText}>
                    <Typography {...styles.label}>
                        {translate("Exp. Year")}:
                    </Typography>
                    <Typography {...styles.expirationText} aria-labelledby="paymentProfileExpiryDate">
                        {props.expiryDate && (() => {
                            const expiry = props.expiryDate;
                            return expiry.substring(expiry.lastIndexOf("/") + 1);
                        })()}
                    </Typography>
                </Typography>
            </Typography>
            <Typography as="span" className="border-seperator"></Typography>
            <Typography as="div" className="card-address" {...styles.cardAddress}>
                <Typography {...styles.billingAddressLabelText} id="paymentProfileBillingAddress">
                    <h3 className="card-heading">{translate("Billing Address")}</h3>
                </Typography>
                <Typography {...styles.billingAddressText} aria-labelledby="paymentProfileBillingAddress">
                    {props.address}
                </Typography>
            </Typography>
        </StyledWrapper>
    );
};

export default PaymentProfileBillingAddress;
